import { useEffect, useState } from 'react';
import { MaandSamenvatting, DagRij, DagDetail, SnapshotHeader, ExportButton, BankImportPreview } from '../components/controle';
import { useControleStore } from '../stores';
import { parseCamt053 } from '../lib/parse-camt053';
import { fetchControleDagtotalen } from '../lib/bank';

function formatAmount(amount: number): string {
  if (amount === 0) return '-';
  return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

function formatMonthLabel(month: string): string {
  const parts = month.split('-').map(Number);
  const d = new Date(parts[0]!, parts[1]! - 1, 1);
  const label = d.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function shiftMonth(month: string, delta: number): string {
  const parts = month.split('-').map(Number);
  const d = new Date(parts[0]!, parts[1]! - 1 + delta, 1);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

export function ControlePage() {
  const {
    selectedMonth, snapshot, dagen, totalen, transacties,
    isLoading, isCreating, isSaving, error,
    verschilCount, expandedDag,
    profiles, excludedProfiles,
    bankImportData, dagNotities,
    loadSnapshot, createSnapshot, refreshSnapshot, approveMonth,
    setMonth, toggleDagDetail, saveDagNotitie, manualMatch, unmatch,
    toggleProfile, setBankImportData, importBankBedragen,
  } = useControleStore();

  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [bankImportError, setBankImportError] = useState<string | null>(null);
  const [bankAutoLoading, setBankAutoLoading] = useState(false);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  const isReadOnly = snapshot?.status === 'goedgekeurd';

  const unmatchedCount = transacties.filter(
    t => t.bron === 'mollie' && !t.is_refund && t.match_status === 'unmatched'
  ).length;

  const handleApprove = () => {
    setShowApproveConfirm(false);
    approveMonth();
  };

  // Bank-kolom vullen vanuit de bank-import (bank_transacties) — geen XML nodig.
  // Synct eerst de hele maand via Enable Banking, aggregeert dan per dag en
  // voedt dezelfde preview/import-flow als de XML-upload.
  const handleBankAutoImport = async () => {
    setBankImportError(null);
    setBankAutoLoading(true);
    try {
      const result = await fetchControleDagtotalen(selectedMonth);
      if (result.creditsByDay.size === 0) {
        const detail = result.summary?.consentProblem
          ? 'De bankkoppeling is verlopen — vernieuw de consent op /financieel.'
          : result.summary?.errors?.length
            ? result.summary.errors[0]
            : 'Geen bankbijschrijvingen gevonden voor deze maand.';
        setBankImportError(`Bank ophalen: ${detail}`);
        return;
      }
      setBankImportData({
        iban: result.iban,
        creditsByDay: result.creditsByDay,
        filteredTotal: result.filteredTotal,
        filteredCount: result.filteredCount,
        entries: result.entries,
      });
    } catch (err) {
      setBankImportError(err instanceof Error ? err.message : 'Bank ophalen mislukt');
    } finally {
      setBankAutoLoading(false);
    }
  };

  const handleBankFileUpload = async (files: File[]) => {
    setBankImportError(null);
    try {
      // Parse all files and merge credits by day + collect individual entries
      const merged = new Map<string, number>();
      const allEntries: { datum: string; bedrag: number; description: string; senderName: string }[] = [];
      let iban = '';
      let filteredTotal = 0;
      let filteredCount = 0;

      for (const file of files) {
        const text = await file.text();
        const result = parseCamt053(text);
        if (!iban) iban = result.iban;
        filteredTotal = Math.round((filteredTotal + result.filteredTotal) * 100) / 100;
        filteredCount += result.filteredCount;

        for (const [date, amount] of result.creditsByDay) {
          if (date.startsWith(selectedMonth)) {
            const current = merged.get(date) || 0;
            merged.set(date, Math.round((current + amount) * 100) / 100);
          }
        }

        // Collect individual credit entries for this month (not filtered)
        for (const entry of result.entries) {
          if (entry.isCredit && !entry.filtered && entry.date.startsWith(selectedMonth)) {
            allEntries.push({
              datum: entry.date,
              bedrag: entry.amount,
              description: entry.description,
              senderName: entry.senderName,
            });
          }
        }
      }

      if (merged.size === 0) {
        setBankImportError(`Geen inkomende transacties gevonden voor ${formatMonthLabel(selectedMonth)} in ${files.length} bestand${files.length !== 1 ? 'en' : ''}`);
        return;
      }

      setBankImportData({ iban, creditsByDay: merged, filteredTotal, filteredCount, entries: allEntries });
    } catch (err) {
      setBankImportError(err instanceof Error ? err.message : 'Fout bij het lezen van de bestanden');
    }
  };

  return (
    <>
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-7xl mx-auto space-y-5">

          {/* Header with month navigation */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-heading font-semibold text-main">Transactie Controle</h1>

            <div className="flex items-center gap-3">
              {/* Export */}
              {snapshot && dagen.length > 0 && (
                <ExportButton month={selectedMonth} dagen={dagen} totalen={totalen} />
              )}

              {/* Month navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMonth(shiftMonth(selectedMonth, -1))}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <span className="text-sm font-medium text-main min-w-[160px] text-center">
                  {formatMonthLabel(selectedMonth)}
                </span>

                <button
                  onClick={() => setMonth(shiftMonth(selectedMonth, 1))}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center gap-3 text-sm text-muted">
              <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              <span>Snapshot laden...</span>
            </div>
          )}

          {/* Error */}
          {(error || bankImportError) && (
            <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-800">{error || bankImportError}</span>
            </div>
          )}

          {/* Snapshot header */}
          {!isLoading && (
            <SnapshotHeader
              snapshot={snapshot}
              isCreating={isCreating}
              isSaving={isSaving}
              onFetch={() => createSnapshot()}
              onRefresh={() => refreshSnapshot()}
              onApprove={() => setShowApproveConfirm(true)}
              onBankImport={handleBankFileUpload}
              onBankAutoImport={handleBankAutoImport}
              isBankLoading={bankAutoLoading}
            />
          )}

          {/* Approve confirmation dialog */}
          {showApproveConfirm && (
            <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <span className="text-amber-800">
                Weet je zeker dat je <strong>{formatMonthLabel(selectedMonth)}</strong> wilt goedkeuren? Dit kan niet ongedaan worden gemaakt.
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowApproveConfirm(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleApprove}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                >
                  Ja, goedkeuren
                </button>
              </div>
            </div>
          )}

          {/* Profile filter */}
          {snapshot && Object.keys(profiles).length > 1 && (
            <div className="flex items-center gap-3 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm">
              <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">Mollie profielen:</span>
              <div className="flex items-center gap-3">
                {Object.entries(profiles).map(([id, name]) => (
                  <label key={id} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!excludedProfiles.has(id)}
                      onChange={() => toggleProfile(id)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className={excludedProfiles.has(id) ? 'text-gray-400 line-through' : 'text-gray-700'}>
                      {name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Verschil + unmatched alerts */}
          {snapshot && !isLoading && (verschilCount > 0 || unmatchedCount > 0) && (
            <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-amber-800">
                {verschilCount > 0 && (
                  <><strong>{verschilCount}</strong> dag{verschilCount !== 1 ? 'en' : ''} met verschil {'>'} &euro;1</>
                )}
                {verschilCount > 0 && unmatchedCount > 0 && ', '}
                {unmatchedCount > 0 && (
                  <><strong>{unmatchedCount}</strong> ongematchte Mollie transactie{unmatchedCount !== 1 ? 's' : ''}</>
                )}
              </span>
            </div>
          )}

          {/* Summary card */}
          {snapshot && (
            <MaandSamenvatting
              totalen={totalen}
              isLoading={isLoading}
            />
          )}

          {/* Daily grid */}
          {snapshot && dagen.length > 0 && (
            <div className="bg-white rounded-card shadow-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80">
                      <th className="py-2 px-2 text-left text-xs font-medium text-muted uppercase tracking-wider w-32">Datum</th>
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider">MyPos</th>
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider">Pin SF</th>
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider w-16">&Delta;</th>
                      <th className="w-px bg-gray-200" />
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider">Mollie</th>
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider">iDEAL SF</th>
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider w-16">&Delta;</th>
                      <th className="w-px bg-gray-200" />
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider">Bank</th>
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider">Bank SF</th>
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider w-16">&Delta;</th>
                      <th className="w-px bg-gray-200" />
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider">Contant</th>
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider">Gecontr.</th>
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider">Envelop</th>
                      <th className="py-2 px-2 text-right text-xs font-medium text-muted uppercase tracking-wider w-16">&Delta;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dagen.map(dag => (
                      <>
                        <DagRij
                          key={dag.datum}
                          dag={dag}
                          isExpanded={expandedDag === dag.datum}
                          isReadOnly={isReadOnly}
                          onToggle={() => toggleDagDetail(dag.datum)}
                          onBankChange={(datum, bedrag) => saveDagNotitie(datum, { bank_bedrag: bedrag })}
                          onContantChange={(datum, bedrag) => saveDagNotitie(datum, { contant_bedrag: bedrag })}
                          onStatusToggle={(datum) => {
                            const current = dag.notitie?.status || 'open';
                            saveDagNotitie(datum, { status: current === 'open' ? 'verklaard' : 'open' });
                          }}
                        />
                        {expandedDag === dag.datum && (
                          <DagDetail
                            key={`detail-${dag.datum}`}
                            datum={dag.datum}
                            transacties={transacties}
                            bankExtern={dag.bank.extern}
                            isReadOnly={isReadOnly}
                            onMatch={manualMatch}
                            onUnmatch={unmatch}
                          />
                        )}
                      </>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 bg-gray-50/80 text-sm font-semibold">
                      <td className="py-2 px-2 text-xs uppercase text-muted">Totaal</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.pin.extern)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.pin.sf)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.pin.verschil)}</td>
                      <td className="w-px bg-gray-200" />
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.ideal.extern)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.ideal.sf)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.ideal.verschil)}</td>
                      <td className="w-px bg-gray-200" />
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.bank.extern)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.bank.sf)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.bank.verschil)}</td>
                      <td className="w-px bg-gray-200" />
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.contant.geregistreerd)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.contant.gecontroleerd)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.contant.envelop)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatAmount(totalen.contant.verschil)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bank import preview modal */}
      {bankImportData && (
        <BankImportPreview
          iban={bankImportData.iban}
          creditsByDay={bankImportData.creditsByDay}
          filteredTotal={bankImportData.filteredTotal}
          filteredCount={bankImportData.filteredCount}
          dagNotities={dagNotities}
          isSaving={isSaving}
          onConfirm={importBankBedragen}
          onCancel={() => setBankImportData(null)}
        />
      )}
    </>
  );
}
